import React from 'react';
import { ThemeSelector } from '../../components/ThemeSelector';
import { sitesApi } from '../../api/client';
import type { Site } from '../../schemas/validation';

interface ThemeSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  site: Site;
  onSiteUpdate: (site: Site) => void;
  onShowToast: (message: string, type: 'success' | 'error') => void;
  /** Called after a successful theme update so the caller can refresh derived state (e.g. unpublished summary) */
  onAfterUpdate?: () => void | Promise<void>;
}

export const ThemeSelectorModal: React.FC<ThemeSelectorModalProps> = ({
  isOpen,
  onClose,
  site,
  onSiteUpdate,
  onShowToast,
  onAfterUpdate,
}) => {
  const [isUpdatingTheme, setIsUpdatingTheme] = React.useState(false);

  if (!isOpen) return null;

  const handleThemeSelect = async (newTheme: string) => {
    try {
      setIsUpdatingTheme(true);
      console.log('🎨 SiteManager: Updating theme to:', newTheme);

      const updatedSite = await sitesApi.update(site.id, {
        theme: newTheme,
      });

      console.log('🎨 SiteManager: API response after theme update:', {
        id: updatedSite.id,
        name: updatedSite.name,
        theme: updatedSite.theme,
        hasTheme: 'theme' in updatedSite,
      });

      // Update local state with the response
      onSiteUpdate(updatedSite);

      // Also update the sites list in localStorage for preview
      const sitesData = localStorage.getItem('sites');
      if (sitesData) {
        const sites = JSON.parse(sitesData);
        const index = sites.findIndex((s: Site) => s.id === site.id);
        if (index !== -1) {
          sites[index] = updatedSite;
          localStorage.setItem('sites', JSON.stringify(sites));
        }
      }

      // Show success message
      onShowToast('Theme updated successfully!', 'success');
      // Refetch unpublished summary so publish badges update immediately
      await onAfterUpdate?.();
      onClose();
    } catch (error) {
      console.error('Failed to update theme:', error);
      onShowToast('Failed to update theme. Please try again.', 'error');
    } finally {
      setIsUpdatingTheme(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={() => !isUpdatingTheme && onClose()}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
          <div className="mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Change Website Theme</h3>
            <p className="mt-1 text-sm text-gray-500">
              Select a new theme for your website. This will update the visual appearance of all
              pages.
            </p>
          </div>

          <ThemeSelector
            selectedTheme={site.theme || 'standard'}
            onThemeSelect={handleThemeSelect}
          />

          <div className="mt-5 sm:mt-6 flex justify-end">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => !isUpdatingTheme && onClose()}
              disabled={isUpdatingTheme}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
